/**
 * Sharing a video earns a video: the visitor's half (backend/api/api/services/referrals.py).
 *
 * Someone who taps "make one like this" on a shared video (/v/<id>) is about to
 * sign up, and sign-up leaves the page: for Clerk's form, and with Google for
 * accounts.google.com and back. The idea survives that in the ?redirect= link;
 * the video they came from is carried the same way (&ref=<id>) and also kept
 * here, because a query parameter that a redirect drops would cost the person
 * who shared the video they were promised.
 *
 * It is written when they tap the button, not when they open the page: looking
 * at a video records nothing about anyone. It is sent to our own API once they
 * have an account, and deleted when the API has answered either way.
 *
 * A personal invite link (?invite=<the inviter's account id>, on any page; the
 * share dialog shows it, since 2026-11-05) is the same referral with no video.
 * Following that link is the tap, so it is kept on arrival. The first of the
 * two wins, as one account can have only one referral.
 */
const KEY = 'cs_ref';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // the API refuses accounts older than this anyway
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** The shared video a link names (?ref=<id>), or null. */
export function referralFromSearch(search) {
  const ref = new URLSearchParams(search || '').get('ref');
  return ref && UUID.test(ref) ? ref.toLowerCase() : null;
}

/** Keep the video they came from. The first one wins: it is what brought them. */
export function rememberReferral(projectId, now = Date.now()) {
  const store = storage();
  if (!store || !projectId || !UUID.test(projectId)) return;
  if (pending(now)) return;
  try {
    store.setItem(KEY, JSON.stringify({ projectId: projectId.toLowerCase(), at: now }));
  } catch {
    // Storage full or blocked: the ?ref= in the link is still there
  }
}

// What this browser still has to claim: { projectId } or { inviterId }, or null
function pending(now) {
  const store = storage();
  if (!store) return null;
  try {
    const saved = JSON.parse(store.getItem(KEY) || 'null');
    const id = saved?.projectId || saved?.inviterId;
    if (!id || !UUID.test(id) || now - saved.at > MAX_AGE_MS) {
      store.removeItem(KEY);
      return null;
    }
    return saved;
  } catch {
    return null;
  }
}

/** The video this browser still has to claim, or null. */
export function pendingReferral(now = Date.now()) {
  return pending(now)?.projectId || null;
}

/** The account whose invite link this browser still has to claim, or null. */
export function pendingInvite(now = Date.now()) {
  return pending(now)?.inviterId || null;
}

/** The inviter a link names (?invite=<account id>), or null. */
export function inviteFromSearch(search) {
  const id = new URLSearchParams(search || '').get('invite');
  return id && UUID.test(id) ? id.toLowerCase() : null;
}

/** Keep the account whose invite link brought them, unless something came first. */
export function rememberInvite(inviterId, now = Date.now()) {
  const store = storage();
  if (!store || !inviterId || !UUID.test(inviterId)) return;
  if (pending(now)) return;
  try {
    store.setItem(KEY, JSON.stringify({ inviterId: inviterId.toLowerCase(), at: now }));
  } catch {
    // Storage full or blocked: the ?invite= in the link is still there
  }
}

/** Someone's personal invite link: their language's home with ?invite=<their account id>. */
export const inviteLink = (code, homePath = '/') => `https://www.clip-short.com${homePath}?invite=${code}`;

export function forgetReferral() {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // nothing to forget
  }
}
